import React from 'react';

import { usePathname } from 'expo-router';
import { ScrollView, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { StandardMobileHeader } from 'components/app-header/mobile-header';
import { ServiceModeBar } from 'components/app-header/mobile-header/service-mode-bar';
import { CenteredRowContainer } from 'components/layout/centered-row-container';
import { useAcceleratorBadgesOnMenuExperiment } from 'experiments/use-accelerator-badges-on-menu-experiment';
import { useIsDesktopBreakpoint } from 'hooks/use-media-query';
import { PromoMessageRibbon } from 'pages/loyalty/loyalty-promo-message-ribbon';
import { CustomEventNames, EventTypes, logButtonClick, logEvent } from 'state/amplitude';
import { ClickEventComponentNames } from 'state/amplitude/constants';
import { t } from 'state/intl/translations';
import { routes } from 'utils/routing';

import { useAcceleratorBadgesOnMenu } from '../hooks/use-accelerator-badges-on-menu';

import { MenuAcceleratorBadge } from './menu-accelerator-badge';
import { MenuNavigationTabLink, MenuNavigationTabText } from './menu-navigation-tabs/styled';

export const MenuTabs = () => {
  const pathname = usePathname();

  const isDesktop = useIsDesktopBreakpoint();

  const logTabSelected = (entry: string, tabItemId: string): void => {
    logEvent(CustomEventNames.TABBED_HEADER, EventTypes.Navigation, {
      'Selected Tab': entry,
    });
    logButtonClick(
      {
        attributes: {
          component: ClickEventComponentNames.PRODUCT_SECTION_TABS,
          componentId: tabItemId,
          name: entry,
        },
      },
      { logDuplicateClickEvent: true }
    );
  };

  // if the app is transitioning to a different screen than the menu,
  // then we should force prevent displaying the menu tabs.
  // if (!isOnMenuRoute) {
  //   return <MenuHeaderTransition />;
  // }

  const mainMenuLabelKey = 'fullMenu';
  const recentsLabelKey = 'recents';
  const favoriteLabelKey = 'favorite';

  const mainMenuTab = {
    tabItemId: mainMenuLabelKey,
    label: t(mainMenuLabelKey),
    to: routes.menu,
  };

  const recentItemsTab = {
    tabItemId: recentsLabelKey,
    label: t(recentsLabelKey),
    to: routes.menuRecent,
  };

  const favoritesTab = {
    tabItemId: favoriteLabelKey,
    label: t(favoriteLabelKey),
    to: routes.favorites,
  };

  const menuTabs = [mainMenuTab, recentItemsTab, favoritesTab].filter(item => item !== null);

  const activeTab = menuTabs.findIndex(item => item?.to === pathname);

  const tabWidth = `${100 / menuTabs.length}%`;

  return (
    <>
      {isDesktop ? (
        // Desktop needs the App header to access the cart
        <StandardMobileHeader />
      ) : (
        <ServiceModeBar hideTopOrderStatusBar />
      )}
      <CenteredRowContainer testID="menu-tabs">
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{ width: '100%' }}
          accessibilityRole="tablist"
        >
          {menuTabs.map(({ label, to, tabItemId }, index) => (
            <MenuNavigationTabLink
              key={label}
              linkPath={to}
              testID={`tabbed-menu-${label}`}
              active={index === activeTab}
              onPress={() => logTabSelected(label, tabItemId)}
              minWidth={tabWidth}
              accessibilityRole="tab"
              aria-selected={index === activeTab}
              accessibilityLabel={index === activeTab ? `${label}, selected` : ''}
            >
              <MenuNavigationTabText
                variant={index === 0 ? 'headerOneLook' : 'headerTwoLook'}
                active={index === activeTab}
              >
                {label}
              </MenuNavigationTabText>
            </MenuNavigationTabLink>
          ))}
        </ScrollView>
      </CenteredRowContainer>
    </>
  );
};

export const MenuTabsBadgesExperiment = () => {
  const pathname = usePathname();

  const {
    experimentEnabled,
    shouldShowFullMenuBadge,
    bonusPoints,
    pointsMultiplier,
    cartRequirement,
  } = useAcceleratorBadgesOnMenu({
    menuData: [],
    skipExposureLogging: false,
  });

  // For the promotion message:
  // - If there are not cart requirements for the promotion, this means it's a full menu promotion and we don't want to display it.
  // - If there are cart requirements, this is not a full menu promotion, hence we display the promotion message.
  const shouldShowPromoMessage = experimentEnabled && cartRequirement.size === 0;

  const isDesktop = useIsDesktopBreakpoint();

  const logTabSelected = (entry: string, tabItemId: string): void => {
    logEvent(CustomEventNames.TABBED_HEADER, EventTypes.Navigation, {
      'Selected Tab': entry,
    });
    logButtonClick(
      {
        attributes: {
          component: ClickEventComponentNames.PRODUCT_SECTION_TABS,
          componentId: tabItemId,
          name: entry,
        },
      },
      { logDuplicateClickEvent: true }
    );
  };

  // if the app is transitioning to a different screen than the menu,
  // then we should force prevent displaying the menu tabs.
  // if (!isOnMenuRoute) {
  //   return <MenuHeaderTransition />;
  // }

  const mainMenuLabelKey = 'fullMenu';
  const recentsLabelKey = 'recents';
  const favoriteLabelKey = 'favorite';

  const mainMenuTab = {
    tabItemId: mainMenuLabelKey,
    label: t(mainMenuLabelKey),
    to: routes.menu,
    showBadge: shouldShowFullMenuBadge && (bonusPoints || pointsMultiplier),
  };

  const recentItemsTab = {
    tabItemId: recentsLabelKey,
    label: t(recentsLabelKey),
    to: routes.menuRecent,
    showBadge: false,
  };

  const favoritesTab = {
    tabItemId: favoriteLabelKey,
    label: t(favoriteLabelKey),
    to: routes.favorites,
    showBadge: false,
  };

  const menuTabs = [mainMenuTab, recentItemsTab, favoritesTab].filter(item => item !== null);

  const activeTab = menuTabs.findIndex(item => item?.to === pathname);

  const tabWidth = `${100 / menuTabs.length}%`;

  return (
    <>
      {isDesktop ? (
        // Desktop needs the App header to access the cart
        <StandardMobileHeader />
      ) : (
        <>
          <ServiceModeBar hideTopOrderStatusBar />
          {shouldShowPromoMessage && <PromoMessageRibbon />}
        </>
      )}
      <CenteredRowContainer testID="menu-tabs">
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{ width: '100%' }}
          accessibilityRole="tablist"
        >
          {menuTabs.map(({ label, to, tabItemId, showBadge }, index) => (
            <MenuNavigationTabLink
              key={label}
              linkPath={to}
              testID={`tabbed-menu-${label}`}
              active={index === activeTab}
              onPress={() => logTabSelected(label, tabItemId)}
              minWidth={tabWidth}
              accessibilityRole="tab"
              aria-selected={index === activeTab}
              accessibilityLabel={index === activeTab ? `${label}, selected` : ''}
            >
              <MenuNavigationTabText
                variant={index === 0 ? 'headerOneLook' : 'headerTwoLook'}
                active={index === activeTab}
              >
                {label}
              </MenuNavigationTabText>
              {showBadge && (
                <MenuAcceleratorBadge
                  bonusPoints={bonusPoints}
                  pointsMultiplier={pointsMultiplier}
                />
              )}
            </MenuNavigationTabLink>
          ))}
        </ScrollView>
      </CenteredRowContainer>
    </>
  );
};

/** TODO
 * - switch these tabs to https://github.com/EvanBacon/expo-router-top-tabs
 * - wrap with CenteredRowContainer
 * */
export const MenuTabsWrap: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { top } = useSafeAreaInsets();

  const enableAcceleratorBadgesExperiment = useAcceleratorBadgesOnMenuExperiment(true);
  const MenuTabsComponent = enableAcceleratorBadgesExperiment ? MenuTabsBadgesExperiment : MenuTabs;

  return (
    <View style={{ flex: 1, paddingTop: top }}>
      <MenuTabsComponent />
      {children}
    </View>
  );
};
